// Auflösung über vite.config.js → resolve.alias auf dist/exceljs.min.js (browser field)
import ExcelJS from "exceljs";

const TEMPLATE_KEYS = [
  "source",
  "kind",
  "timestamp_utc",
  "base",
  "amount",
  "side",
  "quote",
  "quote_amount",
  "price",
  "fee",
  "fee_asset",
  "txid",
  "notes",
  "account_id",
];

const HEADER_LABELS = {
  en: {
    source: "Source (optional)",
    kind: "Type",
    timestamp_utc: "Timestamp UTC",
    base: "Token",
    amount: "Amount",
    side: "Buy / Sell",
    quote: "Quote",
    quote_amount: "Quote Amount",
    price: "Price (optional)",
    fee: "Fee (optional)",
    fee_asset: "Fee Asset (optional)",
    txid: "TXID (optional)",
    notes: "Notes (optional)",
    account_id: "Account ID (optional)",
  },
  de: {
    source: "Quelle (optional)",
    kind: "Art",
    timestamp_utc: "Zeitstempel UTC",
    base: "Token",
    amount: "Menge",
    side: "Kauf / Verkauf",
    quote: "Gegenwert",
    quote_amount: "Gegenwert-Betrag",
    price: "Preis (optional)",
    fee: "Gebühr (optional)",
    fee_asset: "Gebühren-Asset (optional)",
    txid: "TXID (optional)",
    notes: "Notiz (optional)",
    account_id: "Konto-ID (optional)",
  },
};

const HEADER_HELP = {
  source: {
    en: "Optional. Exchange or platform name. If empty, V3Tax uses MANUAL.",
    de: "Optional. Name der Börse oder Plattform. Wenn leer, verwendet V3Tax MANUAL.",
  },
  kind: {
    en: "Allowed values: trade, buy, sell, deposit, withdrawal, income.",
    de: "Erlaubte Werte: trade, buy, sell, deposit, withdrawal, income. Alternativ auch: handel, kauf, verkauf, einzahlung, auszahlung, einnahme.",
  },
  timestamp_utc: {
    en: "Use UTC. Recommended format: YYYY-MM-DD HH:MM:SS",
    de: "Bitte UTC verwenden. Empfohlenes Format: YYYY-MM-DD HH:MM:SS",
  },
  base: {
    en: "Main token, e.g. VET, VTHO, B3TR.",
    de: "Haupt-Token, z. B. VET, VTHO, B3TR.",
  },
  amount: {
    en: "Token amount. Must be greater than 0.",
    de: "Token-Menge. Muss größer als 0 sein.",
  },
  side: {
    en: "Required for trades. Allowed: BUY, SELL, Buy, Sell.",
    de: "Für Trades erforderlich. Erlaubt: BUY, SELL, Kauf, Verkauf.",
  },
  quote: {
    en: "Required for trades. Also required for income if price is filled.",
    de: "Für Trades erforderlich. Auch bei income erforderlich, wenn ein Preis eingetragen wird.",
  },
  quote_amount: {
    en: "For trades, provide quote amount or price.",
    de: "Bei Trades bitte Gegenwert-Betrag oder Preis angeben.",
  },
  price: {
    en: "Optional if quote amount exists.",
    de: "Optional, wenn der Gegenwert-Betrag vorhanden ist.",
  },
  fee: {
    en: "Optional fee amount.",
    de: "Optionale Gebühr.",
  },
  fee_asset: {
    en: "Optional fee asset.",
    de: "Optionales Gebühren-Asset.",
  },
  txid: {
    en: "Optional, but recommended to avoid duplicates.",
    de: "Optional, aber empfohlen, um Duplikate zu vermeiden.",
  },
  notes: {
    en: "Optional internal note.",
    de: "Optionale interne Notiz.",
  },
  account_id: {
    en: "Use the same ID for all files belonging to the same exchange account. Use different IDs for separate accounts or subaccounts.",
    de: "Verwende dieselbe ID für alle Dateien desselben Exchange-Kontos. Unterschiedliche IDs für getrennte Konten oder Subaccounts.",
  },
};

const STANDARD_TEMPLATE = {
  key: "standard",
  fileName: "v3tax-standard-import.xlsx",
  keys: TEMPLATE_KEYS,
  allowedKinds: {
    en: ["trade", "buy", "sell", "deposit", "withdrawal", "income"],
    de: ["trade", "buy", "sell", "deposit", "withdrawal", "income"],
  },
  allowedSides: {
    en: ["BUY", "SELL"],
    de: ["BUY", "SELL"],
  },
  examples: [
    {
      source: "Binance",
      kind: "trade",
      timestamp_utc: "2025-01-20 14:05:00",
      base: "VET",
      amount: "1000",
      side: "BUY",
      quote: "EUR",
      quote_amount: "28.50",
      price: "0.0285",
      fee: "0.05",
      fee_asset: "EUR",
      txid: "trade-buy-001",
      notes: "Spot market",
    },
    {
      source: "Kraken",
      kind: "deposit",
      timestamp_utc: "2025-02-01 12:00:00",
      base: "B3TR",
      amount: "400",
      side: "",
      quote: "",
      quote_amount: "",
      price: "",
      fee: "",
      fee_asset: "",
      txid: "dep-001",
      notes: "",
    },
    {
      source: "",
      kind: "income",
      timestamp_utc: "2025-03-01 08:00:00",
      base: "VTHO",
      amount: "90",
      side: "",
      quote: "EUR",
      quote_amount: "",
      price: "0.0032",
      fee: "",
      fee_asset: "",
      txid: "",
      notes: "Reward",
    },
  ],
};

export const TEMPLATE_VARIANTS = {
  standard: STANDARD_TEMPLATE,
};

function toColumnLetter(num) {
  let n = num;
  let s = "";
  while (n > 0) {
    const mod = (n - 1) % 26;
    s = String.fromCharCode(65 + mod) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function getHeaderLabels(language = "de") {
  const lang = language === "en" ? "en" : "de";
  return TEMPLATE_KEYS.map((key) => HEADER_LABELS[lang][key] || key);
}

function rowFromKeys(keys, obj) {
  return keys.map((key) => obj?.[key] ?? "");
}

function getColumnWidth(key, language = "de") {
  const lang = language === "en" ? "en" : "de";

  const widths = {
    source: lang === "de" ? 20 : 18,
    kind: 16,
    timestamp_utc: 22,
    base: 14,
    amount: 16,
    side: lang === "de" ? 18 : 14,
    quote: lang === "de" ? 18 : 14,
    quote_amount: lang === "de" ? 20 : 18,
    price: 18,
    fee: 16,
    fee_asset: lang === "de" ? 22 : 18,
    txid: 24,
    notes: 24,
    account_id: 22,
  };

  return widths[key] || 16;
}

function styleHeaderRow(ws, headerLabels) {
  headerLabels.forEach((_, index) => {
    const cell = ws.getCell(1, index + 1);
    cell.font = {
      bold: true,
      color: { argb: "1F2937" },
      size: 11,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "F3F4F6" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
    };
    cell.border = {
      top: { style: "thin", color: { argb: "D1D5DB" } },
      left: { style: "thin", color: { argb: "D1D5DB" } },
      bottom: { style: "thin", color: { argb: "D1D5DB" } },
      right: { style: "thin", color: { argb: "D1D5DB" } },
    };
  });
}

function styleBodyRows(ws, startRow, endRow, colCount) {
  for (let row = startRow; row <= endRow; row++) {
    for (let col = 1; col <= colCount; col++) {
      const cell = ws.getCell(row, col);
      cell.alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E5E7EB" } },
        left: { style: "thin", color: { argb: "E5E7EB" } },
        bottom: { style: "thin", color: { argb: "E5E7EB" } },
        right: { style: "thin", color: { argb: "E5E7EB" } },
      };
    }
  }
}

function addTemplateValidations(ws, keys, config, language) {
  const lang = language === "en" ? "en" : "de";
  const kindCol = keys.indexOf("kind") + 1;
  const sideCol = keys.indexOf("side") + 1;

  if (kindCol > 0 && config.allowedKinds?.[lang]?.length) {
    const list = config.allowedKinds[lang].join(",");
    for (let row = 2; row <= 2000; row++) {
      ws.getCell(row, kindCol).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${list}"`],
        showErrorMessage: true,
        errorTitle: lang === "en" ? "Invalid type" : "Ungültige Art",
        error: lang === "en"
          ? "Please choose a valid type from the dropdown."
          : "Bitte wähle eine gültige Art aus der Liste.",
      };
    }
  }

  if (sideCol > 0 && config.allowedSides?.[lang]?.length) {
    const list = config.allowedSides[lang].join(",");
    for (let row = 2; row <= 2000; row++) {
      ws.getCell(row, sideCol).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${list}"`],
        showErrorMessage: true,
        errorTitle: lang === "en" ? "Invalid buy/sell value" : "Ungültiger Kauf/Verkauf-Wert",
        error: lang === "en"
          ? "Please choose a valid buy/sell value from the dropdown."
          : "Bitte wähle einen gültigen Kauf/Verkauf-Wert aus der Liste.",
      };
    }
  }
}

function addHeaderNotes(ws, keys, language) {
  const lang = language === "en" ? "en" : "de";
  keys.forEach((key, index) => {
    const note = HEADER_HELP?.[key]?.[lang];
    if (note) {
      ws.getCell(1, index + 1).note = note;
    }
  });
}

function addExamplesSheet(workbook, config, language) {
  const headerLabels = getHeaderLabels(language);
  const ws = workbook.addWorksheet(language === "en" ? "Examples" : "Beispiele");

  ws.addRow(headerLabels);
  styleHeaderRow(ws, headerLabels);

  config.keys.forEach((key, index) => {
    ws.getColumn(index + 1).width = getColumnWidth(key, language);
  });

  config.examples.forEach((example) => {
    ws.addRow(rowFromKeys(config.keys, example));
  });

  styleBodyRows(ws, 2, 1 + config.examples.length, config.keys.length);

  ws.autoFilter = {
    from: "A1",
    to: `${toColumnLetter(config.keys.length)}1`,
  };
}

function addInstructionsSheet(workbook, _config, language) {
  const ws = workbook.addWorksheet(language === "en" ? "Instructions" : "Hinweise");
  const isEn = language === "en";

  ws.columns = [{ width: 28 }, { width: 100 }];

  const rows = [
    [
      isEn ? "Template" : "Template",
      isEn ? "V3Tax Standard Import" : "V3Tax Standard-Import",
    ],
    [
      isEn ? "Use" : "Verwendung",
      isEn
        ? 'Fill only the "Import" sheet and upload the XLSX file in V3Tax.'
        : 'Bitte nur das Blatt "Import" ausfüllen und die XLSX-Datei in V3Tax hochladen.',
    ],
    [
      isEn ? "Date format" : "Datumsformat",
      "YYYY-MM-DD HH:MM:SS (UTC)",
    ],
    [
      isEn ? "Decimals" : "Dezimalzahlen",
      isEn ? "Use a decimal point, e.g. 1234.56" : "Bitte Dezimalpunkt verwenden, z. B. 1234.56",
    ],
    [
      isEn ? "Trade rule" : "Trade-Regel",
      isEn
        ? "Trades require token, amount, buy/sell, quote and either quote amount or price."
        : "Trades benötigen Token, Menge, Kauf/Verkauf, Gegenwert und entweder Gegenwert-Betrag oder Preis.",
    ],
    [
      isEn ? "Optional fields" : "Optionale Felder",
      isEn
        ? "Headers with '(optional)' can be left empty."
        : "Felder mit '(optional)' können leer bleiben.",
    ],
  ];

  rows.forEach((r, idx) => {
    const row = ws.addRow(r);
    if (idx === 0) {
      row.getCell(1).font = { bold: true };
      row.getCell(2).font = { bold: true };
    }
  });
}

export async function buildTemplateWorkbook(_variant = "standard", language = "de") {
  const config = STANDARD_TEMPLATE;
  const headerLabels = getHeaderLabels(language);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "V3Tax";
  workbook.company = "V3Tax";
  workbook.created = new Date();
  workbook.modified = new Date();

  const importWs = workbook.addWorksheet("Import", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  importWs.addRow(headerLabels);
  styleHeaderRow(importWs, headerLabels);

  config.keys.forEach((key, index) => {
    importWs.getColumn(index + 1).width = getColumnWidth(key, language);
  });

  addTemplateValidations(importWs, config.keys, config, language);

  styleBodyRows(importWs, 2, 2000, config.keys.length);

  importWs.autoFilter = {
    from: "A1",
    to: `${toColumnLetter(config.keys.length)}1`,
  };

  addExamplesSheet(workbook, config, language);
  addInstructionsSheet(workbook, config, language);

  const buffer = await workbook.xlsx.writeBuffer();

  return {
    buffer,
    fileName: config.fileName,
  };
}
